import React, { useState, useEffect, useRef } from 'react';
import './Dashboard.css';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [renewalsData, setRenewalsData] = useState(null);
  
  const productionChartRef = useRef(null);
  const productPerProductChartRef = useRef(null);
  const revenueChartRef = useRef(null);
  const claimsChartRef = useRef(null);
  const clientGrowthChartRef = useRef(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (dashboardData) {
      drawCharts();
    }
  }, [dashboardData, activeTab]);

  useEffect(() => {
    if (activeTab === 'renewals') {
      fetchRenewals();
    }
  }, [activeTab, selectedMonth, selectedYear]);

  const fetchRenewals = async () => {
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/reports/upcoming-renewals?month=${selectedMonth}&year=${selectedYear}`);
      const data = await response.json();
      setRenewalsData(data.data);
    } catch (error) {
      console.error('Error fetching renewals:', error);
    }
  };

  const formatMonthName = (yearMonth) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const [year, month] = yearMonth.split('-');
    return months[parseInt(month) - 1];
  };

  const fetchDashboardData = async () => {
    try {
      const [monthlyProductionRes, productionRes, performanceRes, clientGrowthRes, riskRes, claimsRes] = await Promise.all([
        fetch('https://main.victriedemos.com:3017/api/reports/monthly-production'),
        fetch('https://main.victriedemos.com:3017/api/reports/production-per-product'),
        fetch('https://main.victriedemos.com:3017/api/reports/performance'),
        fetch('https://main.victriedemos.com:3017/api/reports/client-growth'),
        fetch('https://main.victriedemos.com:3017/api/reports/risk-assessment'),
        fetch('https://main.victriedemos.com:3017/api/reports/claims')
      ]);

      const [monthlyProduction, production, performance, clientGrowth, risk, claims] = await Promise.all([
        monthlyProductionRes.json(),
        productionRes.json(),
        performanceRes.json(),
        clientGrowthRes.json(),
        riskRes.json(),
        claimsRes.json()
      ]);

      setDashboardData({
        monthlyProduction: monthlyProduction.data || [],
        production: production.data || [],
        performance: performance,
        clientGrowth: clientGrowth,
        risk: risk,
        claims: claims
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const drawStackedBarChart = (canvas, data1, data2, labels, color1, color2, legend1, legend2) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.clearRect(0, 0, width, height);
    
    // Calculate max value from stacked data
    const stackedValues = data1.map((val, idx) => val + data2[idx]);
    const maxValue = Math.max(...stackedValues, 1); // Minimum 1 to avoid division by zero
    const barWidth = Math.min((width - 100) / data1.length - 10, 60);
    const chartHeight = height - 80;
    
    // Draw legend
    ctx.fillStyle = color1;
    ctx.fillRect(20, 10, 15, 15);
    ctx.fillStyle = '#2c3e50';
    ctx.font = '12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(legend1, 40, 22);
    
    ctx.fillStyle = color2;
    ctx.fillRect(180, 10, 15, 15);
    ctx.fillStyle = '#2c3e50';
    ctx.fillText(legend2, 200, 22);
    
    // Draw bars
    data1.forEach((value1, index) => {
      const value2 = data2[index];
      const totalValue = value1 + value2;
      const totalHeight = (totalValue / maxValue) * chartHeight;
      const height1 = (value1 / maxValue) * chartHeight;
      const height2 = (value2 / maxValue) * chartHeight;
      
      const x = 50 + index * (barWidth + 10);
      const y = height - 60 - totalHeight;
      
      // Draw first stack (Policies)
      ctx.fillStyle = color1;
      ctx.fillRect(x, y, barWidth, height1);
      
      // Draw second stack (Extensions)
      ctx.fillStyle = color2;
      ctx.fillRect(x, y + height1, barWidth, height2);
      
      // Draw total value on top
      if (totalValue > 0) {
        ctx.fillStyle = '#2c3e50';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(totalValue.toFixed(0), x + barWidth / 2, y - 5);
      }
      
      // Draw label
      ctx.save();
      ctx.translate(x + barWidth / 2, height - 30);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.font = '11px Arial';
      ctx.fillText(labels[index], 0, 0);
      ctx.restore();
    });
  };

  const drawBarChart = (canvas, data, labels, color) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.clearRect(0, 0, width, height);
    
    const maxValue = Math.max(...data);
    const barWidth = (width - 60) / data.length - 10;
    const chartHeight = height - 50;
    
    data.forEach((value, index) => {
      const barHeight = (value / maxValue) * chartHeight;
      const x = 40 + index * (barWidth + 10);
      const y = height - 30 - barHeight;
      
      // Draw bar
      ctx.fillStyle = color;
      ctx.fillRect(x, y, barWidth, barHeight);
      
      // Draw value on top
      ctx.fillStyle = '#2c3e50';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(value.toFixed(0), x + barWidth / 2, y - 5);
      
      // Draw label
      ctx.save();
      ctx.translate(x + barWidth / 2, height - 10);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.fillText(labels[index], 0, 0);
      ctx.restore();
    });
  };

  const drawLineChart = (canvas, data, labels, color) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.clearRect(0, 0, width, height);
    
    const maxValue = Math.max(...data);
    const chartHeight = height - 60;
    const chartWidth = width - 80;
    const xStep = chartWidth / (data.length - 1);
    
    // Draw grid lines
    ctx.strokeStyle = '#ecf0f1';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = 30 + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(50, y);
      ctx.lineTo(width - 30, y);
      ctx.stroke();
    }
    
    // Draw line
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    data.forEach((value, index) => {
      const x = 50 + index * xStep;
      const y = 30 + chartHeight - (value / maxValue) * chartHeight;
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    ctx.stroke();
    
    // Draw points
    ctx.fillStyle = color;
    data.forEach((value, index) => {
      const x = 50 + index * xStep;
      const y = 30 + chartHeight - (value / maxValue) * chartHeight;
      
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw labels
      ctx.fillStyle = '#2c3e50';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.save();
      ctx.translate(x, height - 10);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.fillText(labels[index], 0, 0);
      ctx.restore();
    });
  };

  const drawPieChart = (canvas, data, labels, colors) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.clearRect(0, 0, width, height);
    
    const centerX = width / 3;
    const centerY = height / 2;
    const radius = Math.min(centerX, centerY) - 20;
    
    const total = data.reduce((sum, value) => sum + value, 0);
    let currentAngle = -Math.PI / 2;
    
    // Draw slices
    data.forEach((value, index) => {
      const sliceAngle = (value / total) * Math.PI * 2;
      
      ctx.fillStyle = colors[index % colors.length];
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      ctx.fill();
      
      // Draw percentage
      const midAngle = currentAngle + sliceAngle / 2;
      const textX = centerX + (radius * 0.7) * Math.cos(midAngle);
      const textY = centerY + (radius * 0.7) * Math.sin(midAngle);
      
      ctx.fillStyle = 'white';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const percentage = ((value / total) * 100).toFixed(1);
      ctx.fillText(percentage + '%', textX, textY);
      
      currentAngle += sliceAngle;
    });
    
    // Draw legend
    const legendX = (width * 2) / 3;
    let legendY = 30;
    
    labels.forEach((label, index) => {
      ctx.fillStyle = colors[index % colors.length];
      ctx.fillRect(legendX, legendY, 15, 15);
      
      ctx.fillStyle = '#2c3e50';
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(label + ': ' + data[index], legendX + 25, legendY + 10);
      
      legendY += 25;
    });
  };

  const drawCharts = () => {
    if (!dashboardData) return;

    // Monthly Production Stacked Bar Chart (Policies vs Extensions)
    if (productionChartRef.current && activeTab === 'overview') {
      const monthlyData = dashboardData.monthlyProduction || [];
      const policies = monthlyData.map(m => m.NewPolicies);
      const extensions = monthlyData.map(m => m.Extensions);
      const labels = monthlyData.map(m => formatMonthName(m.Month));
      drawStackedBarChart(
        productionChartRef.current, 
        policies, 
        extensions, 
        labels, 
        '#667eea', 
        '#f39c12',
        'New Policies',
        'Extensions'
      );
    }

    // Production Per Product Pie Chart
    if (productPerProductChartRef.current && activeTab === 'overview') {
      const productData = dashboardData.production || [];
      const data = productData.map(p => p.TotalPolicies || 0);
      const labels = productData.map(p => p.ProductName);
      const colors = ['#667eea', '#f39c12', '#2ecc71', '#e74c3c', '#9b59b6', '#3498db', '#1abc9c', '#f1c40f'];
      drawPieChart(productPerProductChartRef.current, data, labels, colors);
    }

    // Revenue Line Chart
    if (revenueChartRef.current && activeTab === 'performance') {
      const revenueData = dashboardData.performance.revenue || [];
      const values = revenueData.map(r => r.MonthlyRevenue);
      const labels = revenueData.map(r => r.Month);
      drawLineChart(revenueChartRef.current, values, labels, '#2ecc71');
    }

    // Claims Pie Chart
    if (claimsChartRef.current && activeTab === 'risk') {
      const claims = dashboardData.claims.summary;
      const data = [
        claims.NewClaims || 0,
        claims.SettledClaims || 0,
        claims.OngoingClaims || 0,
        claims.RejectedClaims || 0
      ];
      const labels = ['New', 'Settled', 'Ongoing', 'Rejected'];
      const colors = ['#3498db', '#2ecc71', '#f39c12', '#e74c3c'];
      drawPieChart(claimsChartRef.current, data, labels, colors);
    }

    // Client Growth Line Chart
    if (clientGrowthChartRef.current && activeTab === 'clients') {
      const clientData = dashboardData.clientGrowth.monthly || [];
      const values = clientData.map(c => c.NewClients);
      const labels = clientData.map(c => c.Month);
      drawLineChart(clientGrowthChartRef.current, values, labels, '#9b59b6');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData ? [
    { 
      title: 'Total Policies', 
      value: dashboardData.performance?.policies?.TotalPolicies || 0, 
      icon: '📋', 
      color: '#3498db',
      subtitle: `${dashboardData.performance?.policies?.ActivePolicies || 0} Active`
    },
    { 
      title: 'Total Claims', 
      value: dashboardData.claims?.summary?.TotalClaims || 0, 
      icon: '📝', 
      color: '#e74c3c',
      subtitle: `${dashboardData.claims?.summary?.SettledClaims || 0} Settled`
    },
    { 
      title: 'Total Clients', 
      value: dashboardData.clientGrowth?.summary?.TotalClients || 0, 
      icon: '👥', 
      color: '#2ecc71',
      subtitle: `${dashboardData.clientGrowth?.summary?.Last30Days || 0} New (30d)`
    },
    { 
      title: 'Total Revenue', 
      value: formatCurrency(dashboardData.performance?.policies?.TotalPremium || 0), 
      icon: '💰', 
      color: '#f39c12',
      subtitle: 'Total Premium'
    }
  ] : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>📊 Dashboard Overview</h1>
          <p className="page-subtitle">Real-time business analytics and insights</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="stat-card" style={{ borderLeft: `4px solid ${stat.color}` }}>
            <div className="stat-icon" style={{ background: `${stat.color}20` }}>
              <span style={{ color: stat.color }}>{stat.icon}</span>
            </div>
            <div className="stat-content">
              <h3>{stat.title}</h3>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-subtitle">{stat.subtitle}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Production Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          📈 Performance
        </button>
        <button 
          className={`tab-btn ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveTab('clients')}
        >
          👥 Client Growth
        </button>
        <button 
          className={`tab-btn ${activeTab === 'renewals' ? 'active' : ''}`}
          onClick={() => setActiveTab('renewals')}
        >
          🔄 Upcoming Renewals
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && dashboardData && (
          <div className="chart-grid">
            <div className="chart-card">
              <h3>📊 Monthly Production: Policies vs Extensions</h3>
              <p className="chart-description">New policies and extensions booked per month (last 12 months)</p>
              <canvas ref={productionChartRef} width="700" height="350"></canvas>
            </div>
            <div className="chart-card">
              <h3>📈 Production Per Product</h3>
              <p className="chart-description">Distribution of policies across products</p>
              <canvas ref={productPerProductChartRef} width="400" height="350"></canvas>
            </div>
          </div>
        )}

        {activeTab === 'performance' && dashboardData && (
          <div className="chart-grid">
            <div className="chart-card full-width">
              <h3>📈 Monthly Revenue Trend</h3>
              <p className="chart-description">Revenue performance over the last 6 months</p>
              <canvas ref={revenueChartRef} width="800" height="300"></canvas>
            </div>
            <div className="performance-stats">
              <div className="perf-card">
                <h4>Total Revenue</h4>
                <p className="perf-value">{formatCurrency(dashboardData.performance?.policies?.TotalPremium || 0)}</p>
              </div>
              <div className="perf-card">
                <h4>Average Premium</h4>
                <p className="perf-value">{formatCurrency(dashboardData.performance?.policies?.AveragePremium || 0)}</p>
              </div>
              <div className="perf-card">
                <h4>Active Policies</h4>
                <p className="perf-value">{dashboardData.performance?.policies?.ActivePolicies || 0}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'clients' && dashboardData && (
          <div className="chart-grid">
            <div className="chart-card">
              <h3>👥 Client Growth Trend</h3>
              <p className="chart-description">New client acquisition over time</p>
              <canvas ref={clientGrowthChartRef} width="600" height="300"></canvas>
            </div>
            <div className="stats-summary">
              <h3>Growth Summary</h3>
              <div className="summary-item highlight">
                <div className="summary-label">Total Clients</div>
                <div className="summary-value">{dashboardData.clientGrowth?.summary?.TotalClients || 0}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">New (Last 30 Days)</div>
                <div className="summary-value">{dashboardData.clientGrowth?.summary?.Last30Days || 0}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">New (Last 90 Days)</div>
                <div className="summary-value">{dashboardData.clientGrowth?.summary?.Last90Days || 0}</div>
              </div>
              <h3 style={{ marginTop: '20px' }}>Recent Months</h3>
              {dashboardData.clientGrowth?.monthly?.slice(0, 4).map((month, index) => (
                <div key={index} className="summary-item">
                  <div className="summary-label">{month.Month}</div>
                  <div className="summary-value">{month.NewClients} clients</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'renewals' && (
          <div className="renewals-container">
            <div className="renewals-header">
              <h3>🔄 Upcoming Policy Renewals</h3>
              <div className="month-selector">
                <label>Select Month: </label>
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
                <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                  {[2024, 2025, 2026, 2027].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {renewalsData && (
              <>
                <div className="renewals-summary">
                  <div className="summary-card">
                    <h4>Total Renewals</h4>
                    <p className="big-number">{renewalsData.count || 0}</p>
                  </div>
                  <div className="summary-card highlight">
                    <h4>Expected Premium</h4>
                    <p className="big-number">{formatCurrency(renewalsData.totalPremium || 0)}</p>
                  </div>
                </div>
                
                <div className="renewals-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Policy No</th>
                        <th>Client Name</th>
                        <th>Product</th>
                        <th>Expiry Date</th>
                        <th>Days to Expiry</th>
                        <th>Premium</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renewalsData.renewals && renewalsData.renewals.length > 0 ? (
                        renewalsData.renewals.map((renewal, index) => (
                          <tr key={index}>
                            <td>{renewal.PolicyNumber}</td>
                            <td>{renewal.ClientName}</td>
                            <td>{renewal.ProductName}</td>
                            <td>{new Date(renewal.PolicyExpiryDate).toLocaleDateString()}</td>
                            <td>
                              <span className={renewal.DaysToExpiry < 30 ? 'urgent' : ''}>
                                {renewal.DaysToExpiry} days
                              </span>
                            </td>
                            <td>{formatCurrency(renewal.GrossPremium)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                            No renewals found for this month
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
